<template>
    <div class="container account-statement">
        <div class="row">
            <div class="col">
                <div class="card content">
                    <div class="options">
                        <div class="payment-options">
                            <div class="sunbit-option">
                                <span
                                    v-if="!isLoading && sunbitLink !== ''"
                                    class="sunbit-text"
                                >
                                    <span class="sunbit-now">Sunbit Now,</span>
                                    <span class="pay-over-time"
                                        >Pay-over-time.</span
                                    >
                                </span>
                                <div
                                    v-if="!isLoading && sunbitLink !== ''"
                                    class="wp-block-buttons"
                                >
                                    <div
                                        class="wp-block-button has-custom-width is-style-primary hero-button"
                                    >
                                        <a
                                            class="wp-block-button__link hero-button-text"
                                            :href="sunbitLink"
                                            target="_blank"
                                            >Prequalify Now!</a
                                        >
                                    </div>
                                </div>
                            </div>
                            <button
                                v-show="showPayNow"
                                class="btn btn-primary pay"
                                :disabled="errorMessage !== '' || isLoading"
                                @click="pay"
                            >
                                PAY NOW
                            </button>
                        </div>
                    </div>

                    <div v-if="isLoading" class="spinner-wrapper">
                        <b-spinner variant="secondary" />
                        <span>Loading...</span>
                    </div>
                    <iframe
                        v-else-if="!isLoading && errorMessage === ''"
                        width="100%"
                        height="100%"
                        :src="file"
                    />
                    <div
                        v-else-if="!isLoading && errorMessage !== ''"
                        class="spinner-wrapper"
                    >
                        <p>{{ errorMessage }}</p>
                    </div>
                </div>

                <div class="switchable__text mt-3">
                    <p class="credit">
                        <img
                            :src="clinicInfo.photo"
                            alt="Logo"
                            style="max-width: 150px;"
                        />
                        <br />
                        <span v-html="clinicInfo.mail.address" />
                        <span v-html="clinicInfo.mail.state" />
                        <span v-html="clinicInfo.mail.zipcode" />
                        <br />
                        {{ clinicInfo.phone }} |
                        <a :href="`mailto:${clinicInfo.email}`">{{
                            clinicInfo.email
                        }}</a>
                    </p>
                </div>
            </div>
        </div>

        <payment-modal
            ref="paymentModal"
            @load="true"
            @on-success="onSuccess"
        />

        <b-modal :visible="showSuccessMessage" size="sm">
            <template v-slot:modal-header>
                <h2 class="success-title">Success</h2>
            </template>
            Thank you for your payment. A receipt has been emailed to you.
            <template v-slot:modal-footer>
                <button
                    class="btn btn-primary"
                    @click="showSuccessMessage = false"
                >
                    Close
                </button>
            </template>
        </b-modal>
    </div>
</template>

<script>
/*
this file is not located within the "layout.vue".
it means that anything in "helper.js" won't work here
unless we explicitly import the file.
for example: "this._message()" don't work
*/

import axios from "axios";
import PaymentModal from "@/components/new-payment/payment-modal";

export default {
    components: {
        PaymentModal
    },
    data() {
        return {
            viewerPath: "pdfjs/2.6.347/web/viewer.html",
            filePath: "",
            isLoading: true,
            errorMessage: "",
            isPayModalOpen: false,
            isLoadingModal: true,
            amount: 0,
            businessId: "",
            ccProcessor: "",
            cardConnectUrl: "",
            clientId: "",
            clinicInfo: {
                mail: {}
            },
            paymentToDate: "",
            isSaving: false,
            currency: "",
            toDate: "",
            paymentTypeId: "",
            paymentTypeName: "",
            showSuccessMessage: false,
            hasPaymentExt: false,
            sunbitLink: ""
        };
    },
    computed: {
        file() {
            return `${this.viewerPath}?file=${this.filePath}`;
        },
        showPayNow() {
            if (this.hasPaymentExt && this.amount > 0) {
                return true;
            } else {
                return false;
            }
        }
    },
    async mounted() {
        document.getElementById("the-heartbeat").style.display = "none";
        document.body.style.backgroundColor = "#fff";
        document.getElementById("app").style.height = "100vh";
        if (this.$route.query.payment_intent) {
            if (this.$route.query.redirect_status === "succeeded") {
                this.showSuccessMessage = true;
            }
            await new Promise(resolve => setTimeout(resolve, 4000));
        }
        this.fetchData(this.$route.params.clientId);
        this.addScript(process.env.GRAVITY_ASSETS);
    },
    methods: {
        onSuccess({ balance }) {
            this.showSuccessMessage = true;
            this.amount = balance; // update balance
        },
        async fetchData(encodedId) {
            try {
                this.isLoading = true;

                const res = await axios.post(
                    `${process.env.BARRAMUNDI}payment/account-statement/${encodedId}`
                );

                const response = res.data.response;

                if (
                    response &&
                    response.message &&
                    response.message.code === 200
                ) {
                    let resource = response.resource;

                    const formData = new FormData();
                    formData.append("business_id", resource.business_id);
                    formData.append("client_id", resource.client_id);
                    formData.append("type", 6); // pre-qual for account statement

                    const sunbitPreQual = await axios.post(
                        `${process.env.BARRAMUNDI}account/payment-types/getsunbitPreQualLink`,
                        formData
                    );

                    if (sunbitPreQual?.data?.response?.message?.code === 200) {
                        this.sunbitLink =
                            sunbitPreQual?.data?.response?.resources?.url || "";
                    }

                    this.filePath = resource.file_url;
                    this.amount = resource.balance;
                    this.ccProcessor = resource.cc_processor;
                    this.cardConnectUrl = resource.cconnect_url;
                    this.currency = resource.currency;
                    this.clinicInfo = resource.clinic_info;
                    this.businessId = resource.business_id;
                    this.clientId = resource.client_id;
                    this.toDate = resource.to_date;
                    this.paymentTypeId = resource.payment_type_id;
                    this.paymentTypeName = resource.payment_type_name;
                    this.hasPaymentExt = resource.payment_extension_enabled;

                    this.isLoading = false;
                } else {
                    throw new Error("Unable to load account statement.");
                }
            } catch (e) {
                this.isLoading = false;
                this.errorMessage = e.message;
            }
        },
        pay() {
            this.$refs.paymentModal.openPayNow({
                currency: this.currency,
                amount: this.amount,
                creditCardProcessor: this.ccProcessor,
                cardConnectUrl: this.cardConnectUrl,
                businessId: this.businessId,
                clientId: this.clientId,
                toDate: this.toDate,
                paymentTypeId: this.paymentTypeId,
                paymentTypeName: this.paymentTypeName
            });
        },
        closeModal() {
            this.isPayModalOpen = false;
        },
        addScript(src) {
            let doc = document.createElement("script");
            doc.setAttribute("src", src + "?ts=" + new Date().getTime());
            document.head.appendChild(doc);
        }
    }
};
</script>

<style scoped>
.account-statement {
    margin: 20px auto;
}
iframe {
    border: none;
}
.credit img {
    margin-bottom: 15px;
}
.credit {
    line-height: 2em;
}
.content {
    height: 800px;
}
button.pay {
    font-size: 1.1em;
    height: 40px;
    width: 150px;
    float: right;
}
.options {
    background-color: #fbfbfb;
    /* height: 100px; */
    border-top-right-radius: inherit;
    border-top-left-radius: inherit;
    border-bottom: 1px solid;
    border-color: inherit;
    padding: 10px;
}
.spinner-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
}
.spinner-wrapper span {
    margin-left: 10px;
}
.spinner-wrapper.modal {
    min-height: 300px;
}
.icon-cus {
    width: 16px;
    height: 16px;
    display: inline-block;
}
.success-wrapper {
    display: grid;
    grid-template-columns: 30px 1fr;
    font-size: 13px;
    align-items: center;
}
h2.success-title {
    font-weight: normal;
    margin: 5px 0;
}
.success-close-btn {
    border: none;
    outline: none;
    background-color: #416574;
    color: #fff;
    padding: 10px 15px;
    border-radius: 5px;
}
@media only screen and (max-width: 480px) {
    button.pay {
        width: 100%;
    }
}
.payment-options {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.sunbit-option {
    display: flex;
    align-items: center;
}

.sunbit-text {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
}

.sunbit-now {
    color: #3c4b85; /* Dark blue color */
    font-weight: bold;
    font-size: 1.1em;
}

.pay-over-time {
    color: #ffa64d; /* Orange color */
    font-size: 1.1em;
}
.wp-block-button.is-style-primary .wp-block-button__link {
    background: #6a9838; /* Green to match image */
    border-color: #6a9838;
    color: #ffffff;
    border-radius: 20px; /* Rounded corners */
    padding: 8px 16px;
    font-weight: bold;
    font-size: 0.9rem;
    text-decoration: none;
    display: inline-block;
    transition: background-color 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Subtle shadow */
}

.wp-block-button.is-style-primary .wp-block-button__link:hover {
    background: #5a8730; /* Slightly darker on hover */
    text-decoration: none;
}

.wp-block-button.has-custom-width {
    width: auto; /* Let button size to content */
}

.wp-block-buttons {
    margin: 0 0 0 10px; /* Adjust left margin to create space from text */
    display: inline-flex;
    cursor: pointer;
}

.hero-button {
    margin: 0;
}
</style>
